import React, { Suspense, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import VideoWidget from 'components/Video/Widget';
import ContentArea from 'components/Course/ContentArea';
import { KALTURA_CONFIGS } from 'config/constants';
import {
  EmptyState,
  EmptyStateHeader,
  EmptyStateIcon,
} from '@patternfly/react-core';
import BookIcon from '@patternfly/react-icons/dist/esm/icons/book-open-icon';
import { useSwipeGesture } from '@ole-ui/ole-ui-components';
import InfoCard from 'components/InfoCard';
import LabCircleIcon from 'img/icons/lab-2-circle.svg';
import AiAssistSelectionTools from '../AiAssistSelectionTools';
import PageSkeletonLoading from '../../skeletons/PageSkeletonLoading';
import CourseStore from '../store';

const ArcadeDemo = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'arcade-demo-component' */ 'components/ArcadeDemo'
    ),
);

const ContentVersionHint = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'content-version-hint' */ 'components/ContentVersionHint'
    ),
);

const SubscriptionComparisonTable = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'subscriptions-comparison-table-component' */ 'components/Subscriptions/ComparisonTable'
    ),
);

type Props = {
  courseStore: CourseStore;
  isVideoPlayerEnabled: boolean;
  setIsHoveringVideoPlayer: (isHovering: boolean) => void;
  isAiAssistEnabled?: boolean;
  isAiQuickMenuEnabled?: boolean;
  setIsAiQuickMenuEnabled?: (isEnabled: boolean) => void;
  /**
   * When true, temporarily hide the AI quick menu (e.g. while a HUD panel/sidebar is open).
   */
  isAiQuickMenuSuppressed?: boolean;
};

const CourseTab = ({
  courseStore,
  isVideoPlayerEnabled,
  setIsHoveringVideoPlayer,
  isAiAssistEnabled = false,
  isAiQuickMenuEnabled,
  setIsAiQuickMenuEnabled,
  isAiQuickMenuSuppressed = false,
}: Props) => {
  const { t } = useTranslation();
  const { ref: swipeGestureRef } = useSwipeGesture<HTMLDivElement>({
    threshold: 100,
    onSwipeLeft: () => {
      courseStore.goToNextPage();
    },
    onSwipeRight: () => {
      courseStore.goToPrevPage();
    },
  });

  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    courseSlug,
    euuid,
    page,
    content,
    isLoadingPage,
    isCurrentPageBlockedForFreeTier,
    buildEnv,
    pageWithVideo,
    canShowVideoPlayer,
    customVideoPlayerId,
    pageInteractiveDemo,
    currentlabGradeStatus,
  } = courseStore;

  const { page_tag: currentPageTag } = page ?? {};

  const {
    labGradeInfoCardVariant,
    labGradeInfoCardMessage,
    labGradeInfoCardDataAnalyticId,
  } = useMemo<{
    labGradeInfoCardVariant: 'info' | 'success' | 'warning';
    labGradeInfoCardMessage: string;
    labGradeInfoCardDataAnalyticId: string;
  }>(() => {
    let variant: 'info' | 'success' | 'warning' = 'info';
    let message = t(
      'Note: This lab is not currently configured to save lab grading results.',
    );
    let dataAnalyticId = 'lab-grade-info-card-not-enabled';

    if (currentlabGradeStatus?.lab_status === 'passed') {
      variant = 'success';
      message = t('Passed');
      dataAnalyticId = 'lab-grade-info-card-passed';
    } else if (currentlabGradeStatus?.lab_status === 'not_passed') {
      variant = 'warning';
      message = t('Not Passed');
      dataAnalyticId = 'lab-grade-info-card-not-passed';
    } else if (currentlabGradeStatus?.lab_status === 'not_graded') {
      variant = 'info';
      message = t('Not Graded');
      dataAnalyticId = 'lab-grade-info-card-not-graded';
    }

    return {
      labGradeInfoCardVariant: variant,
      labGradeInfoCardMessage: message,
      labGradeInfoCardDataAnalyticId: dataAnalyticId,
    };
  }, [currentlabGradeStatus]);

  let contentElement = null;
  if (!isCurrentPageBlockedForFreeTier) {
    if (isLoadingPage) {
      contentElement = (
        <div className="course__skeleton">
          <PageSkeletonLoading />
        </div>
      );
    } else if (!page || !content) {
      contentElement = (
        <EmptyState>
          <EmptyStateHeader
            titleText={t('Could not load page content')}
            headingLevel="h4"
            icon={<EmptyStateIcon icon={BookIcon} />}
          />
        </EmptyState>
      );
    } else {
      contentElement = (
        <div className="course__content-wrapper" ref={swipeGestureRef}>
          <ContentArea
            key={currentPageTag}
            containerRef={contentContainerRef}
            euuid={euuid}
            page={page}
            content={content}
            language={courseStore.currentCourse?.language}
            pageInProgress={isLoadingPage}
          />
        </div>
      );
    }
  }

  return (
    <div id="tab-course-content" className="hud-course-view__tab-content">
      {isCurrentPageBlockedForFreeTier && (
        <div className="free-tier-message-wrapper">
          <p>
            This content is unavailable during your free trial. Click a section
            from Chapter 1 or Chapter 2 in the progress bar above, to return to
            your free trial course content.
          </p>
          <p>
            Please subscribe to the Red Hat Learning Subscription to receive
            access to all of the course content.
          </p>
          <SubscriptionComparisonTable />
        </div>
      )}

      {/*
        Keep the AI selection tools early in the DOM so it wins keyboard tab order
        over embedded controls like the Kaltura video player.
      */}
      {isAiAssistEnabled && !isCurrentPageBlockedForFreeTier && (
        <AiAssistSelectionTools
          key={`ai-tools-${currentPageTag}`}
          containerRef={contentContainerRef}
          isAiQuickMenuEnabled={isAiQuickMenuEnabled}
          setIsAiQuickMenuEnabled={setIsAiQuickMenuEnabled}
          isAiQuickMenuSuppressed={isAiQuickMenuSuppressed}
        />
      )}

      {courseStore.canShowLabGradeInfo && (
        <div className="info-card-wrapper">
          <InfoCard
            iconSrc={LabCircleIcon}
            variant={labGradeInfoCardVariant}
            isInline={currentlabGradeStatus?.lab_status === 'not_enabled'}
            isHidden={currentlabGradeStatus === undefined}
            dataAnalyticId={labGradeInfoCardDataAnalyticId}
            tooltipContent={t('Lab grade results for this lab')}
          >
            {labGradeInfoCardMessage}
          </InfoCard>
        </div>
      )}

      {canShowVideoPlayer &&
        isVideoPlayerEnabled &&
        courseStore.userProgressPage && (
          <div
            id="hud-course-view__video-widget"
            className="hud-course-view__video-widget"
            onMouseEnter={() => setIsHoveringVideoPlayer(true)}
            onMouseLeave={() => setIsHoveringVideoPlayer(false)}
          >
            <VideoWidget
              entryId={pageWithVideo.entry_id}
              partnerId={KALTURA_CONFIGS.partnerId}
              playerId={customVideoPlayerId}
              isPlaylist={Boolean(pageWithVideo?.is_playlist)}
              startTime={courseStore.lastWatchedTimestampInPage}
              userProgressPage={courseStore.userProgressPage}
            />
          </div>
        )}

      {pageInteractiveDemo && (
        <div className="hud-course-view__interactive-demo">
          <Suspense fallback="Loading">
            <ArcadeDemo url={pageInteractiveDemo} />
          </Suspense>
        </div>
      )}
      {contentElement}
      <Suspense fallback="Loading">
        <ContentVersionHint
          courseSlug={courseSlug}
          language={courseStore.currentCourse?.language}
          buildEnv={buildEnv}
        />
      </Suspense>
    </div>
  );
};

export default withErrorBoundary(
  observer(CourseTab),
  ComponentError,
  handleError,
);
